/**
 * Pipeline stages — copied verbatim from `fe-prototype/build/buildData.js`
 * so the SPA's animated build sequence reads identically to the design.
 *
 * Why a static list rather than driving it from the API: the backend only
 * exposes a coarse status (`queued` | `building` | `ready` | …), not
 * per-stage events. We animate the 6 stages at the prototype's cadence;
 * each real status transition we receive over SSE bumps the visible
 * stage forward (capped so the animation never overruns the real build).
 */

export interface BuildStage {
  /** Unique key; mirrors the backend pipeline step name. */
  key: string;
  /** Display label — the headline line ("Tracking you down"). */
  label: string;
  /** Subline beneath the label. */
  sub: string;
  /** Lucide icon name for the stage node. */
  icon: string;
  /** Log line shown when the stage is active or done. */
  log: string;
  /**
   * Optional fallback chip rendered when no real backend signal is
   * available yet. Stages that should stay quiet until live data lands
   * (e.g. ``eval`` waiting on the actor/judge battery) omit this.
   */
  fallbackMetric?: string;
  /** Animation duration for this stage in ms. */
  ms: number;
  /** True for the climactic eval stage — gets a tinted node. */
  isEval?: boolean;
}

export const BUILD_STAGES: BuildStage[] = [
  {
    key: "resolve",
    label: "Tracking you down",
    sub: "Finding your business",
    icon: "search",
    log: "Found your website and your Google listing",
    fallbackMetric: "Website + Google",
    ms: 1400,
  },
  {
    key: "crawl",
    label: "Having a read",
    sub: "Going through your site",
    icon: "book-open",
    log: "Read every page on your site, top to bottom",
    ms: 1700,
  },
  {
    key: "classify",
    label: "Sorting what matters",
    sub: "Picking out the important bits",
    icon: "list-checks",
    log: "Pulled out your services, hours and prices",
    ms: 1500,
  },
  {
    key: "enrich",
    label: "Filling the gaps",
    sub: "Rounding out the details",
    icon: "sparkles",
    log: "Pulled your address, phone, hours and recent reviews off Google",
    ms: 1700,
  },
  {
    key: "synthesize",
    label: "Writing it up",
    sub: "Putting your offsider together",
    icon: "pen-line",
    log: "Wrote your answers, a greeting and a voice that sounds like you",
    ms: 1900,
  },
  {
    key: "eval",
    label: "Taking it for a spin",
    sub: "Giving it a test run",
    icon: "phone-call",
    log: "Checked it against caller-style questions for real-data answers",
    ms: 2100,
    isEval: true,
  },
];
